import { readLocalStorageValue, useLocalStorage } from '@mantine/hooks';
import { VRP_TENANT_ID } from '@vrplatform/ui-common';
import { utc } from '@vrplatform/utils';
import { useCallback } from 'react';
import {
  parseVrpApiBaseUrl,
  ResponseData,
  useApiEndpoint,
  useApiQuery,
  useRegionalCrossTeamApiResolver,
} from '../../api';
import type {
  MeIssueCount,
  MeMembership,
  PartnerContext,
  RichTeam,
  Team,
  User,
} from './team.types';
import { useTeam } from '..';

export function useLocalStorageTeamId(userId: string | undefined) {
  const [teamId, setTeamId, removeTeamId] = useLocalStorage<
    string | undefined | null
  >({
    key: `${userId}_tid`,
    defaultValue: undefined,
    getInitialValueInEffect: false,
  });

  const setStoredTeamId = useCallback(
    (value: string | null | undefined) => {
      if (!value) {
        removeTeamId();
        return;
      }

      setTeamId(value);
    },
    [removeTeamId, setTeamId]
  );

  return [teamId, setStoredTeamId, removeTeamId] as const;
}

const getApiBaseUrlStorageKey = (userId: string, teamId: string) =>
  `${userId}_${teamId}_abu`;

export type MeBootstrap = {
  user: User;
  team: Team | null;
  partnerContext: PartnerContext | null;
  membership: MeMembership | null;
  features: string[];
  issues: MeIssueCount[];
};

export const STORYBOOK_ME_QUERY_KEY = 'session-me';

type MeReturn = ResponseData<'get:/me'>;

type MeRequestVariables = {
  allowTeamFallback: boolean;
  requestedTeamId: string | null;
  preferActiveTeam?: boolean;
};

export const getMeRequestInit = (
  variables: MeRequestVariables,
  signal: AbortSignal
) => ({
  params: {
    query: {
      allowTeamFallback: variables.allowTeamFallback,
      preferActiveTeam: variables.preferActiveTeam ?? false,
    },
  },
  headers: variables.requestedTeamId
    ? { 'x-team-id': variables.requestedTeamId }
    : undefined,
  signal,
});

export const normalizeMe = (me: MeReturn): MeBootstrap => {
  const isSuperAdmin = me.user.type === 'admin';
  // TODO(PRO-16515): revisit whether `isSuperAdmin` needs a distinct API
  // signal instead of deriving both flags from `user.type === 'admin'`.
  const isVrpAdmin = isSuperAdmin;
  // The API contract guarantees `membership.role === 'partner'` means
  // partner-admin-derived access.
  const isPartnerAdmin =
    isSuperAdmin || isVrpAdmin || me.membership?.role === 'partner';

  const team: Team | null = me.team
    ? {
        id: me.team.id,
        name: me.team.name ?? '',
        slug: me.team.slug!,
        dataRegion: me.team.dataRegion,
        apiBaseUrl: me.team.apiBaseUrl,
        type: me.team.type,
        partnerId: me.team.partnerId || VRP_TENANT_ID,
        isPartnerManaged: me.team.partnerId !== null,
        // the API contract guarantees a frontend context whenever the
        // response contains a team
        hasEntities: {
          ...me.frontendContext!,
        },
        isGL: me.team.isGeneralLedger,
        isTest: me.team.isTest,
        status: me.team.status === 'inactive' ? 'inactive' : 'active',
        isOnboarding: me.team.isOnboarding,
        createdAt: me.team.createdAt,
        currency: me.team.defaultCurrency,
        statementStartAt: me.team.statementStartAt,
        effectiveStatementStartAt: me.team.effectiveStatementStartAt,
        statementActiveMonth: me.team.statementActiveMonth,
        defaultRevenueRecognition: me.team.defaultRevenueRecognition,
        longTermStayNights: me.team.longTermStayNights,
        booksClosedAt: me.team.booksClosedAt,
        colorPrimary: me.team.colorPrimary,
        logo: me.team.logo,
        settings: me.team.settings,
        billingPartnerId: me.team.billingPartnerId || me.team.partnerId,
        subscription: {
          cancelledAt: me.team.cancelledAt,
          status: me.team.billingSubscriptionStatus as any,
          paymentMethodType: me.team.billingPaymentMethodType as any,
        },
      }
    : null;

  return {
    user: {
      id: me.user.id,
      type: me.user.type,
      firstName: me.user.firstName ?? null,
      lastName: me.user.lastName ?? null,
      partnerId: me.user.partnerId ?? null,
      ownerAccess:
        me.user.type === 'owner'
          ? me.ownerAccess.map((access) => ({
              contactId: access.contactId,
              ownerId: access.ownerId,
              tenantId: me.team?.id,
              role: access.role,
            }))
          : [],
      isSuperAdmin,
      isPartnerAdmin,
      isVrpAdmin,
      email: me.user.email || '',
      createdAt: me.user.createdAt,
      settings: me.user.settings,
    },
    team,
    partnerContext: me.partnerContext,
    membership: me.membership,
    features: me.features,
    issues: me.issues,
  };
};

/**
 * Session bootstrap query. Resolves the authenticated user plus the active
 * team in a single request. Standalone sessions allow the API to fall back from
 * an inaccessible requested team; direct-token sessions require an exact team.
 * The returned `team.id` remains authoritative in both modes.
 */
export function useMeQuery(args: {
  allowTeamFallback?: boolean;
  preferActiveTeam?: boolean;
  storybookQueryKey?: string;
  userId: string | undefined;
  requestedTeamId: string | null | undefined;
}) {
  const getRegionalApi = useRegionalCrossTeamApiResolver();
  const { apiHost } = useApiEndpoint();

  return useApiQuery(
    async (api, signal, variables) => {
      // Start on the regional host this team was served from last time. A
      // stale value is corrected by the 421 replay in the API client. A stored
      // value that is not a VRPlatform API host is dropped and the bootstrap fails.
      const storageKey =
        variables.userId && variables.requestedTeamId
          ? getApiBaseUrlStorageKey(variables.userId, variables.requestedTeamId)
          : null;
      const storedApiBaseUrl = storageKey
        ? readLocalStorageValue<string | null>({
            key: storageKey,
            defaultValue: null,
          })
        : null;
      if (storageKey && storedApiBaseUrl) {
        try {
          parseVrpApiBaseUrl(storedApiBaseUrl, apiHost);
        } catch (error) {
          localStorage.removeItem(storageKey);
          throw error;
        }
      }
      const regionalApi = storedApiBaseUrl
        ? getRegionalApi(storedApiBaseUrl)
        : null;
      const res = await (regionalApi ?? api).GET(
        '/me',
        getMeRequestInit(variables, signal)
      );

      if (res.error) throw res.error;

      const me = normalizeMe(res.data);

      if (variables.userId && me.team) {
        localStorage.setItem(
          getApiBaseUrlStorageKey(variables.userId, me.team.id),
          JSON.stringify(me.team.apiBaseUrl)
        );
      }

      return me;
    },
    {
      skip: !args.userId,
      variables: {
        allowTeamFallback: args.allowTeamFallback ?? true,
        preferActiveTeam: args.preferActiveTeam ?? false,
        userId: args.userId,
        requestedTeamId: args.requestedTeamId ?? null,
      },
      // the bootstrap runs before a team id is installed in the API
      // provider; the requested team participates in the key via variables
      teamScoped: false,
      queryGroup: 'teams',
      uniqueQueryKey: 'me',
      queryKeys: ['users', 'teams', 'featureApprovals'],
      storybookQueryKey: args.storybookQueryKey,
    }
  );
}

export const STORYBOOK_RICH_TEAM_QUERY_KEY = 'session-rich-team';

type ApiRichTeamSource = Pick<
  ResponseData<'get:/team'>,
  | 'id'
  | 'statementStartAt'
  | 'effectiveStatementStartAt'
  | 'migratedFromTenantId'
  | 'historicalStatementsImportedAt'
  | 'companyName'
  | 'members'
  | 'email'
  | 'supportEmail'
  | 'phone'
  | 'taxId'
  | 'billingAddress'
  | 'statementAddress'
  | 'defaultRevenueRecognition'
  | 'longTermStayNights'
  | 'ownerPortalShowDraftStatements'
  | 'issues'
  | 'extractableConnections'
  | 'partner'
  | 'billingPlan'
  | 'enabledFeatures'
  | 'operations'
>;

export const mapApiTeamToRichTeam = (apiTeam: ApiRichTeamSource): RichTeam => ({
  id: apiTeam.id,
  statementStartAt:
    apiTeam.statementStartAt && utc(apiTeam.statementStartAt).isValid()
      ? utc(apiTeam.statementStartAt).yyyymmdd()
      : null,
  effectiveStatementStartAt:
    apiTeam.effectiveStatementStartAt &&
    utc(apiTeam.effectiveStatementStartAt).isValid()
      ? utc(apiTeam.effectiveStatementStartAt).yyyymmdd()
      : null,
  migratedFromTenantId: apiTeam.migratedFromTenantId ?? null,
  historicalStatementsImportedAt:
    apiTeam.historicalStatementsImportedAt ?? null,
  companyName: apiTeam.companyName,
  members: apiTeam.members,
  email: apiTeam.email,
  supportEmail: apiTeam.supportEmail ?? apiTeam.email ?? null,
  phone: apiTeam.phone,
  taxId: apiTeam.taxId,
  billingAddress: apiTeam.billingAddress,
  statementAddress: apiTeam.statementAddress,
  defaultRevenueRecognition: apiTeam.defaultRevenueRecognition ?? 'checkIn',
  longTermStayNights: apiTeam.longTermStayNights ?? null,
  ownerPortalSettings: {
    showDraftStatementsOnSummaries:
      apiTeam.ownerPortalShowDraftStatements ?? false,
  },
  issues: apiTeam.issues,
  extractableConnections: apiTeam.extractableConnections,
  partnerName: apiTeam.partner?.name,
  billingPlan: apiTeam.billingPlan,
  enabledFeatures: apiTeam.enabledFeatures,
  operations: apiTeam.operations,
});

/**
 * Rich team query for route- or interaction-specific fields that are not
 * part of the `/me` bootstrap contract. Must be mounted explicitly (and
 * gated behind open/entity state in globally mounted drawers); normal
 * startup must never issue this request.
 */
export function useTeamQuery(options?: { skip?: boolean }) {
  const [{ id: teamId, apiBaseUrl }] = useTeam();

  return useApiQuery(
    async (api, signal, args) => {
      const res = await api.GET('/team', {
        signal,
        headers: {
          'x-team-id': args.teamId,
        },
      });

      if (res.error) throw res.error;

      const apiTeam = res.data;

      return { team: mapApiTeamToRichTeam(apiTeam) };
    },
    {
      skip: options?.skip || !teamId,
      variables: {
        teamId,
      },
      keepPreviousData: true,
      queryGroup: 'teams',
      uniqueQueryKey: 'team',
      queryKeys: ['teams'],
      // The team's own cell. For partner and VRP admin sessions the session
      // host follows the selected portfolio region instead.
      apiTarget: 'regional',
      regionalApiBaseUrl: apiBaseUrl,
      teamScoped: false,
      storybookQueryKey: STORYBOOK_RICH_TEAM_QUERY_KEY,
    }
  );
}
